import React, { useMemo } from "react";
import { Image } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { FiSearch, FiUser, FiLogOut, FiSettings } from "react-icons/fi";
import logo from "../img/tmis_logo_orig.png";
import "./TmisLayout.css";

const defaultLoginUser = {
  name: "User",
  role: "Trainer",
  email: "",
  initials: "U",
};

const navItems = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Send Performance", path: "/performance" },
  { label: "Performance Template", path: "/template" },
  { label: "Audit Logs", path: "/audit-logs" },
];

const getInitials = (name = "") => {
  const initials = name
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  return initials || "U";
};

const getStoredUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");

    if (!storedUser) return defaultLoginUser;

    const parsedUser = JSON.parse(storedUser);

    return {
      ...defaultLoginUser,
      ...parsedUser,
      name:
        parsedUser.name ||
        parsedUser.user_name ||
        parsedUser.display_name ||
        defaultLoginUser.name,
      role: parsedUser.role || defaultLoginUser.role,
      email: parsedUser.email || "",
      initials:
        parsedUser.initials ||
        getInitials(
          parsedUser.name || parsedUser.user_name || parsedUser.display_name
        ),
    };
  } catch (error) {
    console.error("Failed to read login user:", error);
    return defaultLoginUser;
  }
};

const TmisLayout = ({
  children,
  loginUser,
  searchText = "",
  onSearchChange,
  searchPlaceholder = "Search students, course, trainer...",
  showSidebar = true,
  rightPanel,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentUser = useMemo(() => {
    return loginUser || getStoredUser();
  }, [loginUser]);

  const isActivePath = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }

    return location.pathname === path;
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("authToken");

    navigate("/");
  };

  return (
    <div className="tmis-page accessible-page">
      <header className="top-header" aria-label="Main header">
        <div className="brand-box">
          <Image src={logo} className="brand-logo" alt="TMIS Logo" />

          <div className="brand-divider"></div>

          <div className="brand-title">
            <h1>Student Assessment</h1>
            <span>Assessment Management System</span>
          </div>
        </div>

        <label className="top-search" aria-label="Search">
          <FiSearch aria-hidden="true" />
          <input
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          />
        </label>

        <div className="header-user-summary" aria-label="Logged in user">
          <div className="header-user-avatar" aria-hidden="true">
            {currentUser.initials}
          </div>

          <div className="header-user-text">
            <strong>{currentUser.name}</strong>
            <span>{currentUser.role}</span>
          </div>
        </div>

        <div className="top-icons" aria-label="Account actions">
          <button type="button" aria-label="Settings">
            <FiSettings />
          </button>

          <button type="button" aria-label="Profile">
            <FiUser />
          </button>

          <button type="button" aria-label="Logout" onClick={handleLogout}>
            <FiLogOut />
          </button>
        </div>
      </header>

      <nav className="nav-tabs" aria-label="Dashboard navigation">
        {navItems.map((item) => (
          <button
            key={item.path}
            className={isActivePath(item.path) ? "active" : ""}
            type="button"
            onClick={() => navigate(item.path)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <main
        className={`dashboard-main ${!showSidebar ? "no-sidebar" : ""} ${
          !rightPanel ? "no-right-panel" : ""
        }`}
      >
        {showSidebar && <TmisSidebar loginUser={currentUser} />}

        <section className="dashboard-content">{children}</section>

        {rightPanel && <aside className="right-panel">{rightPanel}</aside>}
      </main>
    </div>
  );
};

export const TmisSidebar = ({ loginUser = defaultLoginUser }) => {
  const currentUser = {
    ...defaultLoginUser,
    ...loginUser,
    initials:
      loginUser.initials ||
      getInitials(loginUser.name || loginUser.user_name || loginUser.email),
  };

  return (
    <aside className="profile-panel" aria-label="Trainer information">
      <div className="profile-card accessible-card">
        <div className="avatar" aria-hidden="true">
          {currentUser.initials}
        </div>

        <h3>{currentUser.name}</h3>
        <p>{currentUser.role}</p>
        <span>{currentUser.email}</span>

        <div className="profile-actions">
          <button type="button">Edit</button>
          <button type="button">View more</button>
        </div>
      </div>

      <div className="switch-box accessible-card">
        <span>Me</span>
        <div className="toggle-switch" aria-hidden="true">
          <span></span>
        </div>
        <span>My Class</span>
      </div>

      <div className="small-card accessible-card">
        <h5>{currentUser.department || "Programme Management Dept"}</h5>
        <div className="person-row">
          <div className="mini-avatar" aria-hidden="true">
            H
          </div>
          <span>Harold</span>
        </div>
      </div>

      <div className="small-card accessible-card">
        <h5>Direct Reports</h5>
        <div className="report-avatar-row">
          <div className="mini-avatar">A</div>
          <div className="mini-avatar">B</div>
          <div className="mini-avatar">C</div>
          <div className="more-avatar">+3</div>
        </div>
      </div>
    </aside>
  );
};

export const TmisRightPanel = ({
  loginUser,
  total = 0,
  completed = 0,
  undone = 0,
}) => {
  const currentUser = loginUser || getStoredUser();

  return (
    <>
      <section className="side-review-card accessible-card">
        <h2>Reviews</h2>
        <div className="review-status">
          <span className="green-dot"></span>
          <div>
            <h3>Performance Check In</h3>
            <p>{currentUser.name}</p>
            <small>Completed 6 days ago</small>
          </div>
          <span className="percent-badge">100%</span>
        </div>
      </section>

      <section className="side-review-card accessible-card">
        <h2>Summary</h2>
        <div className="summary-row">
          <span>Total Students</span>
          <b>{total}</b>
        </div>
        <div className="summary-row">
          <span>Completed</span>
          <b className="summary-completed">{completed}</b>
        </div>
        <div className="summary-row">
          <span>Need Attention</span>
          <b className="summary-undone">{undone}</b>
        </div>
      </section>
    </>
  );
};

export default TmisLayout;